import React, { useState, useEffect, useMemo } from 'react'
import {
  Box,
  Typography,
  Button,
  IconButton,
  Avatar,
  Chip,
  Grid,
  useTheme,
  CircularProgress,
} from '@mui/material'
import { AutoAwesome, Image } from '@mui/icons-material'
import { styled, keyframes } from '@mui/material/styles'
import { useNavigate } from 'react-router-dom'
import { SearchType, Topic } from '@/types'
import { TopicType, PrivacyType, TopicTypes } from '@/utils/constants'
import HeroSectionComponent from './components/HeroSection'
import SVG from 'react-inlinesvg'
import { SVGS } from '@/assets/svgs'
import { UserAvatar } from '@/components'
import { getTopicNameFromTopic, getTopicTypeLabel, isValidAvatarUrl } from '@/utils/helper'
import { useSubscription } from '@/context/SubscriptionContext'
import { FEATURE_KEYS } from '@/utils/subscriptions'
import { BentoCard, TopicTag } from '@/components/StyledComponents'
import { useAuth } from '@/hooks/common'
import TopicStatMini from '@/components/TopicStatsMini'
import { IMAGES } from '@/assets/images'
import { useTopicsListViewModel } from '@/hooks/api/dashboard/viewModels/useTopicsListViewModel'
import { TopicsCacheKey } from '@/utils/cacheKeys'
import { DISCOVER_TABS } from '@/utils/homePageFilterTabs'

// ==================== ANIMATIONS ====================

const ANIMATION_DURATION = 350
const STAGGER_DELAY = 20
const MAX_STAGGER_DELAY = 200

const float = keyframes`
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-20px); }
`

const scaleIn = keyframes`
  from {
    opacity: 0;
    transform: scale(0.9);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`

const slideUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`

const pulse = keyframes`
  0%, 100% {
    transform: scale(1);
    opacity: 0.8;
  }
  50% {
    transform: scale(1.1);
    opacity: 1;
  }
`

// ==================== STYLED COMPONENTS ====================
const PageContainer = styled(Box)(({ theme }) => ({
  minHeight: '100vh',
  background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
  position: 'relative',
  overflow: 'hidden',

  '&::before': {
    content: '""',
    position: 'absolute',
    top: '-50%',
    right: '-10%',
    width: '800px',
    height: '800px',
    background: 'radial-gradient(circle, rgba(99, 102, 241, 0.08) 0%, transparent 70%)',
    borderRadius: '50%',
    animation: `${float} 20s ease-in-out infinite`,
    pointerEvents: 'none',
  },

  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: '-30%',
    left: '-5%',
    width: '600px',
    height: '600px',
    background: 'radial-gradient(circle, rgba(168, 85, 247, 0.06) 0%, transparent 70%)',
    borderRadius: '50%',
    animation: `${float} 25s ease-in-out infinite`,
    animationDelay: '5s',
    pointerEvents: 'none',
  },
}))

const ContentWrapper = styled(Box)({
  position: 'relative',
  zIndex: 1,
  maxWidth: '1600px',
  margin: '0 auto',
  padding: '40px 32px',
})

const CategoryChip = styled(Chip)(({ theme }) => ({
  height: '38px',
  borderRadius: '12px',
  fontSize: '13px',
  fontWeight: 600,
  letterSpacing: '-0.01em',
  padding: '0 6px',
  backgroundColor: '#ffffff',
  border: '2px solid #e2e8f0',
  backdropFilter: 'blur(10px)',
  transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
  boxShadow: '0 2px 6px rgba(15, 23, 42, 0.04)',
  cursor: 'pointer',

  '& .MuiChip-icon': {
    marginLeft: '6px',
    color: '#64748b',
    fontSize: '18px',
    transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
  },

  '&:hover': {
    backgroundColor: '#ffffff',
    borderColor: 'rgba(99, 102, 241, 0.4)',
    transform: 'translateY(-1px)',
    boxShadow: '0 6px 16px rgba(99, 102, 241, 0.15)',

    '& .MuiChip-icon': {
      color: '#6366f1',
      transform: 'scale(1.1) rotate(-5deg)',
    },
  },

  '&:active': {
    transform: 'translateY(0) scale(0.98)',
  },

  '&.MuiChip-filled': {
    background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
    color: '#ffffff',
    border: '2px solid transparent',
    boxShadow: '0 4px 12px rgba(99, 102, 241, 0.35)',

    '& .MuiChip-icon': {
      color: '#ffffff',
    },

    '&:hover': {
      background: 'linear-gradient(135deg, #5558e3 0%, #7c48e8 100%)',
      transform: 'translateY(-2px)',
      boxShadow: '0 6px 20px rgba(99, 102, 241, 0.45)',
    },

    '&:active': {
      transform: 'translateY(-1px) scale(0.98)',
    },
  },
}))

const SectionTitle = styled(Typography)({
  fontSize: '28px',
  fontWeight: 700,
  color: '#0f172a',
  letterSpacing: '-0.02em',
  marginBottom: '20px',
  display: 'flex',
  alignItems: 'center',
  gap: '12px',

  '& .icon': {
    width: '32px',
    height: '32px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '10px',
    background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
    color: '#ffffff',
    fontSize: '18px',
  },
})

const CreateButton = styled(Button)(() => ({
  background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
  color: '#ffffff',
  fontWeight: 600,
  fontSize: '14px',
  padding: '10px 20px',
  borderRadius: '8px',
  textTransform: 'none',
  boxShadow: '0 2px 8px rgba(99, 102, 241, 0.25)',
  border: 'none',
  '&:hover': {
    background: 'linear-gradient(135deg, #5b21b6 0%, #7c3aed 100%)',
    boxShadow: '0 4px 12px rgba(99, 102, 241, 0.35)',
    transform: 'translateY(-1px)',
  },
}))

const FeaturedCard = styled(Box)(({ theme }) => ({
  position: 'relative',
  borderRadius: '20px',
  overflow: 'hidden',
  cursor: 'pointer',
  backgroundColor: '#ffffff',
  border: '2px solid rgba(226, 232, 240, 0.8)',
  boxShadow: '0 8px 24px rgba(0, 0, 0, 0.08)',
  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
  height: '100%',

  '&::before': {
    content: '""',
    position: 'absolute',
    inset: 0,
    borderRadius: '20px',
    padding: '2px',
    background: 'linear-gradient(135deg, rgba(99, 102, 241, 0) 0%, rgba(168, 85, 247, 0) 100%)',
    WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
    WebkitMaskComposite: 'xor',
    maskComposite: 'exclude',
    opacity: 0,
    transition: 'opacity 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
  },

  '&:hover': {
    transform: 'translateY(-8px)',
    borderColor: 'transparent',
    boxShadow: `
      0 20px 60px rgba(99, 102, 241, 0.2),
      0 0 0 1px rgba(99, 102, 241, 0.1)
    `,

    '&::before': {
      opacity: 1,
      background:
        'linear-gradient(135deg, rgba(99, 102, 241, 0.6) 0%, rgba(168, 85, 247, 0.6) 100%)',
    },

    '& .featured-image': {
      transform: 'scale(1.05)',
    },

    '& .featured-overlay': {
      opacity: 1,
    },

    '& .featured-action': {
      opacity: 1,
      transform: 'translateY(0)',
    },
  },
}))

const StatBadge = styled(Box)({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '6px',
  padding: '6px 12px',
  borderRadius: '8px',
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  backdropFilter: 'blur(8px)',
  fontSize: '13px',
  fontWeight: 600,
  color: '#ffffff',
  pointerEvents: 'none',

  '& svg': {
    fontSize: '16px',
  },
})

const CreatorAvatar = styled(Avatar)({
  width: 32,
  height: 32,
  border: '2px solid #ffffff',
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
  transition: 'transform 0.2s cubic-bezier(0.4, 0, 0.2, 1)',

  '&:hover': {
    transform: 'scale(1.15)',
    zIndex: 2,
  },
})

const TrendingBadge = styled(Box)({
  position: 'absolute',
  top: '12px',
  right: '12px',
  padding: '6px 12px',
  borderRadius: '10px',
  background: 'linear-gradient(135deg, #f59e0b 0%, #ef4444 100%)',
  color: '#ffffff',
  fontSize: '12px',
  fontWeight: 700,
  letterSpacing: '0.5px',
  textTransform: 'uppercase',
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
  boxShadow: '0 4px 12px rgba(245, 158, 11, 0.4)',
  zIndex: 2,
  pointerEvents: 'none',

  '& svg': {
    fontSize: '16px',
  },
})

const GradientButton = styled(Button)({
  height: '48px',
  borderRadius: '12px',
  fontSize: '15px',
  fontWeight: 600,
  letterSpacing: '-0.01em',
  textTransform: 'none',
  background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
  color: '#ffffff',
  border: '2px solid transparent',
  boxShadow: '0 4px 16px rgba(99, 102, 241, 0.3)',
  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
  padding: '0 24px',

  '&:hover': {
    background: 'linear-gradient(135deg, #5558e3 0%, #7c48e8 100%)',
    transform: 'translateY(-2px)',
    boxShadow: '0 6px 24px rgba(99, 102, 241, 0.4)',
  },

  '&:active': {
    transform: 'translateY(0) scale(0.98)',
  },
})

// ==================== INTERFACES ====================
interface DiscoverHomeProps {
  topicType?: TopicType
  privacyType?: PrivacyType
}

const categories = [...DISCOVER_TABS]

// ==================== MAIN COMPONENT ====================
export const DiscoverHome: React.FC<DiscoverHomeProps> = ({
  topicType = TopicType.ALL,
  privacyType = PrivacyType.PUBLIC,
}) => {
  const navigate = useNavigate()
  const theme = useTheme()
  const { requirePro, isFeatureAvailable } = useSubscription()
  const showCollections = isFeatureAvailable(FEATURE_KEYS.COLLECTIONS)
  const { user: currentUser } = useAuth()

  const [searchQuery, setSearchQuery] = useState('')
  const [selectedCategoryIndex, setSelectedCategoryIndex] = useState(
    !topicType ? 0 : categories.findIndex((el) => el.value == topicType),
  )
  const selectedTopicType = categories[selectedCategoryIndex].value
  const patternTopicsVM = useTopicsListViewModel(TopicsCacheKey.Discover_Patterns)
  const collectionTopicListVM = useTopicsListViewModel(TopicsCacheKey.Discover_Collections)
  const pinCollectionsTopicListVM = useTopicsListViewModel(TopicsCacheKey.Discover_Pins)

  const {
    getTopics: getPatternTopics,
    topicsList: patternTopics,
    topicsCount: totalPatterns,
    loading: loadingPatterns,
    loadingNextPage: loadingNextPagePatterns,
    loadNextPageData: loadNextPagePatterns,
    canLoadNextPage: canLoadNextPagePatterns,
    pagination: patternsPagination,
  } = patternTopicsVM

  const {
    getTopics: getCollectionTopics,
    topicsList: collectionTopics,
    topicsCount: totalCollections,
    loading: loadingCollections,
    loadingNextPage: loadingNextPageCollections,
    loadNextPageData: loadNextPageCollections,
    canLoadNextPage: canLoadNextPageCollections,
    pagination: collectionsPagination,
  } = collectionTopicListVM

  const {
    getTopics: getPinCollections,
    topicsList: pinCollectionTopics,
    topicsCount: totalPinCollections,
    loading: loadingPinCollections,
    loadingNextPage: loadingNextPagePinCollections,
    loadNextPageData: loadNextPagePinCollections,
    canLoadNextPage: canLoadNextPagePinCollections,
    pagination: pinsPagination,
  } = pinCollectionsTopicListVM

  const isLoading = loadingPatterns || loadingCollections || loadingPinCollections

  useEffect(() => {
    if (!patternsPagination) fetchPatterns(1)
    if (!pinsPagination) fetchPins(1)
  }, [])

  useEffect(() => {
    if (showCollections && !collectionsPagination) fetchAlbums(1)
  }, [showCollections])

  const fetchPatterns = (pageNo = 1) => {
    getPatternTopics({
      pageNo,
      privacyType: PrivacyType.PUBLIC,
      topicType: TopicType.PATTERN,
      searchType: SearchType.LoggedInUser,
    })
  }
  const fetchAlbums = (pageNo = 1) => {
    if (!showCollections) return

    getCollectionTopics({
      pageNo,
      privacyType: PrivacyType.PUBLIC,
      topicType: TopicType.ALBUM,
      searchType: SearchType.LoggedInUser,
    })
  }

  const fetchPins = (pageNo = 1) => {
    getPinCollections({
      pageNo,
      privacyType: PrivacyType.PUBLIC,
      topicType: TopicType.PIN_COLLECTION,
      searchType: SearchType.LoggedInUser,
    })
  }

  const hasMore = useMemo(() => {
    if (selectedTopicType == TopicType.ALL)
      return canLoadNextPagePatterns || canLoadNextPageCollections || canLoadNextPagePinCollections
    if (selectedTopicType == TopicType.PATTERN) return canLoadNextPagePatterns
    if (selectedTopicType == TopicType.ALBUM) return canLoadNextPageCollections
    if (selectedTopicType == TopicType.PIN_COLLECTION) return canLoadNextPagePinCollections
  }, [
    canLoadNextPagePatterns,
    canLoadNextPageCollections,
    canLoadNextPagePinCollections,
    selectedTopicType,
  ])

  const onClickLoadMore = () => {
    if (!loadingPatterns && canLoadNextPagePatterns) {
      loadNextPagePatterns()
    }

    if (!loadingCollections && canLoadNextPageCollections) {
      loadNextPageCollections()
    }

    if (!loadingPinCollections && canLoadNextPagePinCollections) {
      loadNextPagePinCollections()
    }
  }

  // Filter topics by category
  const filteredTopics = useMemo(() => {
    let filtered: Topic[] = []
    if (selectedTopicType == TopicType.ALL) {
      filtered = [...patternTopics, ...collectionTopics, ...pinCollectionTopics]
    } else if (selectedTopicType == TopicType.PATTERN) {
      filtered = patternTopics
    } else if (selectedTopicType == TopicType.ALBUM) {
      filtered = collectionTopics
    } else if (selectedTopicType == TopicType.PIN_COLLECTION) {
      filtered = pinCollectionTopics
    }

    filtered = filtered.sort((a, b) => Date.parse(b.dateUpdated) - Date.parse(a.dateUpdated))

    if (searchQuery) {
      filtered = filtered.filter((topic: Topic) => {
        const topicName = getTopicNameFromTopic(topic)
        return topicName.toLowerCase().includes(searchQuery.toLowerCase())
      })
    }

    return filtered
  }, [
    patternTopics,
    collectionTopics,
    pinCollectionTopics,
    selectedCategoryIndex,
    searchQuery,
    showCollections,
  ])

  const handleTopicClick = (topic: Topic) => {
    // ORIGINAL behavior: ALL topics navigate to /preview with topicID and topicType
    // The preview page handles whether to show edit or view mode based on ownership
    navigate(`/preview?topicID=${topic.topicID}&topicType=${topic.topicType}`)
  }

  // Page titles based on topic type
  const pageTitle = useMemo(() => {
    if (topicType === TopicType.PATTERN) return 'Discover Patterns'
    if (topicType === TopicType.ALBUM) return `Discover ${TopicTypes[TopicType.ALBUM].tabTitle}`
    if (topicType === TopicType.GALLERY) return 'Discover Galleries'
    return 'Discover Amazing Creations'
  }, [topicType])

  const pageDescription = useMemo(() => {
    if (topicType === TopicType.PATTERN)
      return 'Explore thousands of beautiful patterns and designs created by talented designers from around the world.'
    if (topicType === TopicType.ALBUM)
      return 'Browse stunning photo albums and collections shared by our creative community.'
    if (topicType === TopicType.GALLERY)
      return 'Discover inspiring galleries showcasing the best creative work.'
    return 'Explore thousands of beautiful patterns, collections, and galleries created by talented designers from around the world.'
  }, [topicType])

  // Theme colors based on topic type
  const themeColors = useMemo(() => {
    if (topicType === TopicType.PATTERN) {
      return {
        primary: '#8b5cf6', // purple
        secondary: '#a78bfa',
        light: 'rgba(139, 92, 246, 0.12)',
        icon: <AutoAwesome />,
        gradients: [
          'linear-gradient(135deg, rgba(139, 92, 246, 0.12) 0%, rgba(167, 139, 250, 0.12) 100%)',
          'linear-gradient(135deg, rgba(167, 139, 250, 0.15) 0%, rgba(196, 181, 253, 0.15) 100%)',
          'linear-gradient(135deg, rgba(196, 181, 253, 0.15) 0%, rgba(139, 92, 246, 0.15) 100%)',
          'linear-gradient(135deg, #8b5cf6 0%, #a78bfa 50%, #c4b5fd 100%)',
        ],
      }
    }
    if (topicType === TopicType.ALBUM) {
      return {
        primary: '#f97316', // vibrant orange
        secondary: '#fb923c',
        light: 'rgba(249, 115, 22, 0.12)',
        icon: <SVG src={SVGS.TabAlbum} width={16} height={16} />,
        gradients: [
          'linear-gradient(135deg, rgba(249, 115, 22, 0.12) 0%, rgba(251, 146, 60, 0.12) 100%)',
          'linear-gradient(135deg, rgba(251, 146, 60, 0.15) 0%, rgba(253, 186, 116, 0.15) 100%)',
          'linear-gradient(135deg, rgba(253, 186, 116, 0.15) 0%, rgba(249, 115, 22, 0.15) 100%)',
          'linear-gradient(135deg, #f97316 0%, #fb923c 50%, #fdba74 100%)',
        ],
      }
    }
    if (topicType === TopicType.GALLERY) {
      return {
        primary: '#14b8a6', // teal
        secondary: '#2dd4bf',
        light: 'rgba(20, 184, 166, 0.12)',
        icon: <Image />,
        gradients: [
          'linear-gradient(135deg, rgba(20, 184, 166, 0.12) 0%, rgba(45, 212, 191, 0.12) 100%)',
          'linear-gradient(135deg, rgba(45, 212, 191, 0.15) 0%, rgba(94, 234, 212, 0.15) 100%)',
          'linear-gradient(135deg, rgba(94, 234, 212, 0.15) 0%, rgba(20, 184, 166, 0.15) 100%)',
          'linear-gradient(135deg, #14b8a6 0%, #2dd4bf 50%, #5eead4 100%)',
        ],
      }
    }
    // Default (All)
    return {
      primary: '#6366f1', // indigo
      secondary: '#8b5cf6',
      light: 'rgba(99, 102, 241, 0.12)',
      icon: <AutoAwesome />,
      gradients: [
        'linear-gradient(135deg, rgba(99, 102, 241, 0.12) 0%, rgba(168, 85, 247, 0.12) 100%)',
        'linear-gradient(135deg, rgba(168, 85, 247, 0.15) 0%, rgba(236, 72, 153, 0.15) 100%)',
        'linear-gradient(135deg, rgba(236, 72, 153, 0.15) 0%, rgba(99, 102, 241, 0.15) 100%)',
        'linear-gradient(135deg, #6366f1 0%, #8b5cf6 50%, #ec4899 100%)',
      ],
    }
  }, [topicType])

  const handleCategorySelection = (category: any, idx: number) => {
    const proceedSelection = () => {
      navigate(`/discover/${category.id}`)
      setSelectedCategoryIndex(idx)
    }

    if (category.value == TopicType.ALBUM) requirePro(FEATURE_KEYS.COLLECTIONS, proceedSelection)
    else proceedSelection()
  }

  return (
    <PageContainer>
      <ContentWrapper>
        {/* HERO SECTION */}
        <HeroSectionComponent
          topicType={topicType}
          pageTitle={pageTitle}
          pageDescription={pageDescription}
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          themeColors={themeColors}
        >
          {/* Category Filters */}
          <Box sx={{ display: 'flex', mt: 4, gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
            {categories.map((category, idx) => {
              const isSelected = selectedCategoryIndex == idx
              return (
                <CategoryChip
                  key={category.id}
                  label={category.label}
                  icon={isSelected ? category.activeIcon : category.defaultIcon}
                  onClick={() => handleCategorySelection(category, idx)}
                  variant={isSelected ? 'filled' : 'outlined'}
                />
              )
            })}
          </Box>
        </HeroSectionComponent>

        {isLoading && filteredTopics?.length == 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
            <CircularProgress size={48} thickness={3} sx={{ color: '#6366f1' }} />
          </Box>
        )}

        <>
          {filteredTopics.length > 0 && (
            <Box>
              <SectionTitle>
                <Box className='icon'>
                  <AutoAwesome />
                </Box>
                Recently Added
              </SectionTitle>

              <Grid container spacing={2.5}>
                {filteredTopics.map((topic: Topic, index: number) => {
                  const isMyTopic = topic.userID == currentUser.userID

                  // const imageURL = topic.latestEgo?.photoAlbumURL || topic.profilePicThumbURL
                  const lastFirstEgo = topic.firstNote?.egoList?.length
                    ? topic.firstNote.egoList[topic.firstNote.egoList.length - 1]
                    : undefined

                  const lastLatestEgo = topic.latestNote?.egoList?.length
                    ? topic.latestNote.egoList[topic.latestNote.egoList.length - 1]
                    : undefined

                  const imageURL =
                    topic.latestEgo?.photoAlbumURL ||
                    lastLatestEgo?.photoAlbumURL ||
                    lastLatestEgo?.photoFullURL ||
                    lastFirstEgo?.photoAlbumURL ||
                    lastFirstEgo?.photoFullURL ||
                    (isValidAvatarUrl(topic.profilePicThumbURL)
                      ? topic.profilePicThumbURL
                      : IMAGES.topicPreviewPlaceholder)

                  return (
                    <Grid item xs={12} sm={6} md={4} lg={3} key={topic.topicID}>
                      <BentoCard
                        onClick={() => handleTopicClick(topic)}
                        sx={{
                          animation: `${scaleIn} ${ANIMATION_DURATION}ms cubic-bezier(0.4, 0, 0.2, 1) ${Math.min(
                            index * STAGGER_DELAY,
                            MAX_STAGGER_DELAY,
                          )}ms backwards`,
                        }}
                      >
                        <Box
                          className='bento-image'
                          sx={{
                            height: '250px',
                            backgroundImage: imageURL
                              ? `url(${imageURL})`
                              : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            backgroundSize: 'cover',
                            backgroundPosition: 'center',
                            transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          }}
                        />

                        <Box sx={{ padding: '14px' }}>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              pt: 1.5,
                              borderTop: '1px solid rgba(226, 232, 240, 0.6)',
                            }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                              <UserAvatar
                                borderRadius={100}
                                name={topic.userName}
                                url={topic.profilePicThumbURL}
                                size={24}
                              />
                              <Typography
                                sx={{
                                  fontSize: '12px',
                                  fontWeight: 600,
                                  color: '#64748b',
                                }}
                              >
                                {isMyTopic ? 'You' : topic.userName}
                              </Typography>
                              <TopicTag
                                size='small'
                                themeColor={TopicTypes[Number(topic.topicType)].color}
                                label={getTopicTypeLabel(topic.topicType)}
                                sx={{ mx: 1 }}
                              />
                            </Box>

                            <TopicStatMini topic={topic} />
                          </Box>
                        </Box>
                      </BentoCard>
                    </Grid>
                  )
                })}
              </Grid>
              {hasMore && (
                <Box sx={{ textAlign: 'center', mt: 4 }}>
                  <CreateButton
                    onClick={onClickLoadMore}
                    disabled={isLoading}
                    sx={{ minWidth: '200px' }}
                  >
                    {isLoading ? (
                      <CircularProgress size={20} sx={{ color: '#ffffff' }} />
                    ) : (
                      'Load More'
                    )}
                  </CreateButton>
                </Box>
              )}
            </Box>
          )}
          {filteredTopics.length === 0 && !isLoading && (
            <Box
              sx={{
                textAlign: 'center',
                py: 10,
              }}
            >
              <Typography
                sx={{
                  fontSize: '18px',
                  fontWeight: 600,
                  color: '#64748b',
                }}
              >
                No creations found
              </Typography>
            </Box>
          )}
        </>
      </ContentWrapper>
    </PageContainer>
  )
}

export default DiscoverHome
